import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Button } from "@bcc/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bcc/components/ui/dialog";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { connectShopify } from "@bcc/lib/shopify-service";
import { AlertCircle, Loader2, Store } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setShopifyConnectModalTestId } from "./shopify-connect-modal.utils";

interface ShopifyConnectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
  testId?: string;
}

export function ShopifyConnectModal({
  open,
  onOpenChange,
  onConnected,
  testId,
}: ShopifyConnectModalProps) {
  const { t } = useTranslation();
  const [shopDomain, setShopDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    setError(null);
    setLoading(true);

    try {
      const result = await connectShopify(shopDomain);

      if (result.success) {
        onConnected();
        onOpenChange(false);
      } else {
        setError(
          result.error || t("products.shopifyConnect.errors.failedToConnect"),
        );
      }
    } catch (err) {
      setError(t("products.shopifyConnect.errors.unexpectedError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        testId={testId ?? setShopifyConnectModalTestId("content")}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Store className="h-5 w-5 text-primary" />
            </div>
            <DialogTitle>{t("products.shopifyConnect.title")}</DialogTitle>
          </div>
          <DialogDescription>
            {t("products.shopifyConnect.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="shop-domain">
              {t("products.shopifyConnect.shopDomain")}
            </Label>
            <Input
              id="shop-domain"
              placeholder="yourstore.myshopify.com"
              value={shopDomain}
              onChange={(e) => setShopDomain(e.target.value)}
              disabled={loading}
            />
            <p className="text-sm text-muted-foreground">
              {t("products.shopifyConnect.shopDomainHelper")}
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConnect} disabled={loading || !shopDomain}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("products.shopifyConnect.connectStore")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
